import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  Flame,
  Radio,
  ChevronDown,
  Activity,
  Database,
  Cpu,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth, apiClient } from "../App";
import TopPerformingOpportunitiesTable from "../components/opportunities/TopPerformingOpportunitiesTable";
import OpportunityCompactCard from "../components/opportunities/OpportunityCompactCard";
import { growthTierForOpportunity, rankingScore } from "../lib/opportunityMarketRanking";

/** Inline heat row (compact cards — no heavy pills / glow). */
const HEAT_INLINE = {
  hot: {
    icon: <Flame className="h-3 w-3 shrink-0 text-rose-400" aria-hidden />,
    label: "Hot",
    text: "text-rose-300/90",
  },
  rising: {
    icon: <TrendingUp className="h-3 w-3 shrink-0 text-emerald-400" aria-hidden />,
    label: "Rising",
    text: "text-emerald-300/90",
  },
  stable: {
    icon: <Minus className="h-3 w-3 shrink-0 text-zinc-500" aria-hidden />,
    label: "Stable",
    text: "text-zinc-400",
  },
  cooling: {
    icon: <TrendingDown className="h-3 w-3 shrink-0 text-sky-400" aria-hidden />,
    label: "Cooling",
    text: "text-sky-300/90",
  },
};

const HEAT_FILTERS = ["all", "hot", "rising", "stable", "cooling"];
const OPPS_CACHE_KEY = "opportunities_list_cache_v1";

const OpportunitiesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterNiche, setFilterNiche] = useState("all");
  const [filterHeat, setFilterHeat] = useState("all");
  const [sortBy, setSortBy] = useState("potential");
  const [openWhy, setOpenWhy] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [leaderboardSnapshot, setLeaderboardSnapshot] = useState(null);
  const [leaderboardSnapshotLoading, setLeaderboardSnapshotLoading] = useState(true);
  const [leaderboardSnapshotError, setLeaderboardSnapshotError] = useState(null);
  const debugPlanEnabled = localStorage.getItem("plan_override_enabled") === "true";
  const overridePlan = (localStorage.getItem("test_plan_override") || "").trim().toLowerCase();
  const hasAdminPin = Boolean((localStorage.getItem("admin_plan_pin") || "").trim());
  const canUsePlanOverride = debugPlanEnabled && hasAdminPin;
  const currentPlan =
    canUsePlanOverride && ["free", "pro", "full_access"].includes(overridePlan)
      ? overridePlan
      : (user?.plan || "free").toLowerCase();
  const isFreePlan = currentPlan === "free";

  const fetchLeaderboardSnapshot = useCallback(async () => {
    setLeaderboardSnapshotLoading(true);
    setLeaderboardSnapshotError(null);
    try {
      const { data } = await apiClient.get("/opportunities/leaderboard-snapshot");
      setLeaderboardSnapshot(data);
    } catch (e) {
      setLeaderboardSnapshot(null);
      setLeaderboardSnapshotError(e?.response?.data?.detail || e?.message || "Could not load leaderboard.");
    } finally {
      setLeaderboardSnapshotLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
    fetchLastRefresh();
    fetchLeaderboardSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, filterNiche, user?.plan]);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("sort", sortBy);
      if (filterNiche !== "all") params.append("niche", filterNiche);
      params.append("limit", "100");
      const response = await apiClient.get(`/opportunities?${params.toString()}`, { timeout: 10000 });
      setOpportunities(response.data);
      localStorage.setItem(
        OPPS_CACHE_KEY,
        JSON.stringify({
          data: Array.isArray(response.data) ? response.data : [],
          ts: Date.now(),
          sort: sortBy,
          niche: filterNiche,
          free: isFreePlan,
        })
      );
    } catch (error) {
      console.error("Error fetching opportunities:", error?.response?.status, error?.response?.data, error);
      try {
        const raw = localStorage.getItem(OPPS_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.data && Array.isArray(parsed.data)) {
          setOpportunities(parsed.data);
        }
      } catch {
        /* no local cache fallback */
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLastRefresh = async () => {
    try {
      const response = await apiClient.get("/admin/refresh-jobs?limit=1");
      const job = response.data.jobs?.[0];
      if (job) setLastRefresh(job);
    } catch {
      /* ignore */
    }
  };

  const freeBasePool = opportunities;

  const filtered = freeBasePool
    .filter((opp) => {
      if (filterNiche !== "all" && opp.niche !== filterNiche) {
        return false;
      }

      if (filterHeat !== "all") {
        const tier = growthTierForOpportunity(opp);
        if (tier !== filterHeat) return false;
      }

      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        opp.name?.toLowerCase().includes(q) ||
        opp.short_description?.toLowerCase().includes(q) ||
        opp.niche?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sortBy === "potential") {
        return rankingScore(b) - rankingScore(a);
      }
      if (sortBy === "demand") {
        return (Number(b.trend_score) || 0) - (Number(a.trend_score) || 0);
      }
      if (sortBy === "momentum") {
        return (Number(b.momentum) || 0) - (Number(a.momentum) || 0);
      }
      if (sortBy === "lowest_competition") {
        const level = { low: 0, medium: 1, high: 2 };
        const compA = level[String(a.competition || "").toLowerCase()] ?? 1;
        const compB = level[String(b.competition || "").toLowerCase()] ?? 1;
        if (compA !== compB) return compA - compB;
        return rankingScore(b) - rankingScore(a);
      }
      if (sortBy === "newest") {
        const timeA = new Date(a.last_signal_at || a.last_scored_at || a.created_at || 0).getTime();
        const timeB = new Date(b.last_signal_at || b.last_scored_at || b.created_at || 0).getTime();
        return timeB - timeA;
      }
      if (sortBy === "discussed") {
        return (Number(b.engagement_raw) || Number(b.signal_count) || 0) - (Number(a.engagement_raw) || Number(a.signal_count) || 0);
      }

      return rankingScore(b) - rankingScore(a);
    });

  const visibleOpportunities = filtered;
  const verifiedCount = visibleOpportunities.filter((opp) => Number(opp?.display_mrr_usd ?? opp?.market_avg_mrr_usd) > 0).length;
  const hotCount = visibleOpportunities.filter((opp) => growthTierForOpportunity(opp) === "hot").length;
  const avgRadarScore =
    visibleOpportunities.length > 0
      ? Math.round(
          visibleOpportunities.reduce((sum, opp) => sum + (Number(opp?.opportunity_score) || rankingScore(opp) * 100 || 0), 0) /
            visibleOpportunities.length
        )
      : 0;

  const niches = [...new Set(freeBasePool.map((o) => o.niche).filter(Boolean))].sort();
  const formatRelative = (iso) => {
    if (!iso) return "Never";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading && opportunities.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#7C3AED] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-500 text-sm">Loading opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full px-3 py-4 pb-[calc(2rem+env(safe-area-inset-bottom))] md:p-8 md:pb-8 lg:p-10 lg:pb-12">
      {/* Header */}
      <div className="relative mb-5 overflow-hidden rounded-[1.75rem] bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.14),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.1),transparent_30%),linear-gradient(180deg,rgba(255,255,255,0.035),rgba(255,255,255,0.012))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] md:mb-8 md:rounded-[2rem] md:border md:border-white/[0.08] md:p-8 md:shadow-[0_24px_80px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.05)]">
        <div className="pointer-events-none absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)] [background-size:44px_44px]" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-black/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400 md:mb-4 md:border md:border-white/[0.08]">
              <Radio className="h-3.5 w-3.5 text-emerald-400" />
              Live opportunity radar
            </div>
          <h1
              className="mb-2 text-3xl font-semibold tracking-[-0.045em] text-white md:mb-3 md:text-5xl lg:text-6xl"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Opportunities
          </h1>
            <p className="max-w-2xl text-sm leading-6 text-zinc-400 md:text-lg md:leading-7">
              Signal-ranked SaaS markets with verified peer revenue, growth pressure, and build context in one technical view.
          </p>
          {lastRefresh && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
              <Radio className="w-3 h-3 text-[#00E676]" />
              <span>Last refresh: {formatRelative(lastRefresh.finished_at || lastRefresh.started_at)}</span>
              <span className="text-zinc-700">·</span>
              <span>{lastRefresh.scored_opportunities || 0} opportunities indexed</span>
            </div>
          )}
          </div>
          <div className="grid grid-cols-3 gap-1.5 lg:min-w-[360px]">
            <div className="rounded-2xl bg-black/18 p-2.5 md:border md:border-white/[0.08] md:bg-black/20 md:p-3">
              <Database className="mb-1.5 h-3.5 w-3.5 text-violet-300 md:h-4 md:w-4" />
              <p className="text-lg font-semibold text-white tabular-nums md:text-xl">{visibleOpportunities.length}</p>
              <p className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 md:text-[10px] md:tracking-[0.14em]">Visible</p>
            </div>
            <div className="rounded-2xl bg-black/18 p-2.5 md:border md:border-white/[0.08] md:bg-black/20 md:p-3">
              <Activity className="mb-1.5 h-3.5 w-3.5 text-emerald-300 md:h-4 md:w-4" />
              <p className="text-lg font-semibold text-white tabular-nums md:text-xl">{verifiedCount}</p>
              <p className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 md:text-[10px] md:tracking-[0.14em]">Verified</p>
            </div>
            <div className="rounded-2xl bg-black/18 p-2.5 md:border md:border-white/[0.08] md:bg-black/20 md:p-3">
              <Cpu className="mb-1.5 h-3.5 w-3.5 text-sky-300 md:h-4 md:w-4" />
              <p className="text-lg font-semibold text-white tabular-nums md:text-xl">{avgRadarScore || "—"}</p>
              <p className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 md:text-[10px] md:tracking-[0.14em]">Radar avg</p>
            </div>
          </div>
        </div>
      </div>

      <TopPerformingOpportunitiesTable
        snapshot={leaderboardSnapshot}
        loadingSnapshot={leaderboardSnapshotLoading}
        snapshotError={leaderboardSnapshotError}
      />

      {/* Search + desktop selects in one grid (single search field, no duplicate controls) */}
      <div className="mb-3 rounded-[1.25rem] bg-transparent p-0 md:mb-4 md:rounded-[1.5rem] md:border md:border-white/[0.08] md:bg-white/[0.03] md:p-3 md:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] md:backdrop-blur">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-zinc-500" />
          <Input
            placeholder="Search opportunities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-11 min-w-0 rounded-2xl border-white/[0.08] bg-black/25 pl-10 text-sm text-white placeholder:text-zinc-600 md:h-auto md:min-h-0 md:py-6 md:pl-12 md:text-base"
            data-testid="search-input"
          />
        </div>
        <div className="hidden md:block min-w-0">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              className="w-full rounded-2xl border-white/[0.08] bg-black/25 py-6"
              data-testid="sort-filter-desktop"
            >
              <Sparkles className="w-4 h-4 mr-2 text-zinc-500 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111111] border-[#1F1F1F]">
              <SelectItem value="potential">Market intelligence</SelectItem>
              <SelectItem value="demand">Highest Demand</SelectItem>
              <SelectItem value="momentum">Fastest Momentum</SelectItem>
              <SelectItem value="lowest_competition">Lowest Competition</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="discussed">Most Discussed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="hidden md:block min-w-0">
          <Select value={filterNiche} onValueChange={setFilterNiche}>
            <SelectTrigger
              className="w-full rounded-2xl border-white/[0.08] bg-black/25 py-6"
              data-testid="niche-filter-desktop"
            >
              <Filter className="w-4 h-4 mr-2 text-zinc-500 shrink-0" />
              <SelectValue placeholder="All Niches" />
            </SelectTrigger>
            <SelectContent className="bg-[#111111] border-[#1F1F1F]">
              <SelectItem value="all">All Niches</SelectItem>
              {niches.map((niche) => (
                <SelectItem key={niche} value={niche}>
                  {niche}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      </div>

      {/* Mobile: sort, niche & status inside a collapsible panel (vertical scroll only) */}
      <div className="mb-5 md:hidden">
        <Collapsible open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full justify-between rounded-2xl border-white/[0.08] bg-white/[0.035] px-4 text-white hover:bg-white/[0.05]"
              data-testid="mobile-filters-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <Filter className="w-4 h-4 text-zinc-500" />
                Sort, niche & heat
              </span>
              <ChevronDown
                className={`w-4 h-4 text-zinc-500 transition-transform duration-200 ${mobileFiltersOpen ? "rotate-180" : ""}`}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden">
            <div className="mt-3 space-y-4 rounded-2xl bg-white/[0.025] p-3">
              <div className="space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Sort</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger
                    className="h-11 w-full rounded-xl border-white/[0.08] bg-black/20 text-sm"
                    data-testid="sort-filter"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-zinc-500 shrink-0" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#1F1F1F]">
                    <SelectItem value="potential">Market intelligence</SelectItem>
                    <SelectItem value="demand">Highest Demand</SelectItem>
                    <SelectItem value="momentum">Fastest Momentum</SelectItem>
                    <SelectItem value="lowest_competition">Lowest Competition</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="discussed">Most Discussed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Niche</span>
                <Select value={filterNiche} onValueChange={setFilterNiche}>
                  <SelectTrigger
                    className="h-11 w-full rounded-xl border-white/[0.08] bg-black/20 text-sm"
                    data-testid="niche-filter"
                  >
                    <Filter className="w-4 h-4 mr-2 text-zinc-500 shrink-0" />
                    <SelectValue placeholder="All Niches" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#1F1F1F]">
                    <SelectItem value="all">All Niches</SelectItem>
                    {niches.map((niche) => (
                      <SelectItem key={niche} value={niche}>
                        {niche}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-2">
                  Heat (growth)
                </span>
                <div className="flex flex-wrap gap-2">
                  {HEAT_FILTERS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFilterHeat(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        filterHeat === s
                          ? "border-[#7C3AED] bg-[#7C3AED] text-white"
                          : "border-transparent bg-white/[0.035] text-zinc-400 hover:text-white"
                      }`}
                      data-testid={`heat-filter-${s}`}
                    >
                      {s === "all" ? "All" : HEAT_INLINE[s]?.label || s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <div className="mb-6 hidden flex-wrap items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.025] p-1.5 md:inline-flex">
        <span className="px-3 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Heat</span>
        {HEAT_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilterHeat(s)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
              filterHeat === s
                ? "border-[#7C3AED] bg-[#7C3AED] text-white shadow-[0_8px_22px_rgba(124,58,237,0.25)]"
                : "border-transparent bg-transparent text-zinc-500 hover:bg-white/[0.05] hover:text-white"
            }`}
            data-testid={`heat-filter-desktop-${s}`}
          >
            {s === "all" ? "All" : HEAT_INLINE[s]?.label || s}
          </button>
        ))}
      </div>

      {/* Results count */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 md:mb-4">
        <p className="text-sm text-zinc-500" data-testid="results-count">
          Showing <span className="font-medium text-zinc-300">{visibleOpportunities.length}</span> opportunities
          {hotCount > 0 ? <span className="text-zinc-600"> · {hotCount} hot signals</span> : null}
        </p>
        <p className="text-xs uppercase tracking-[0.18em] text-zinc-700">Market signal grid</p>
      </div>

      {/* Opportunities — compact grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {visibleOpportunities.map((opp) => {
          const opportunityId = opp.opportunity_id || opp.id;
          return (
            <OpportunityCompactCard
              key={opportunityId}
              opp={opp}
              onCardClick={() => {
                if (!opportunityId) {
                  console.error("Missing opportunity id for navigation:", opp);
                  return;
                }
                navigate(`/dashboard/opportunities/${opportunityId}`);
              }}
              onWhyClick={(e) => {
                e.stopPropagation();
                setOpenWhy(opp);
              }}
              dataTestId={`opportunity-card-${opportunityId}`}
            />
          );
        })}
      </div>
      {filtered.length === 0 && !loading && (
        <div className="text-center py-16">
          <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {opportunities.length === 0
              ? "Discovering opportunities..."
              : "No opportunities match your filters"}
          </h3>
          <p className="text-zinc-500 mb-4">
            {opportunities.length === 0
              ? "Our ingestion pipeline is fetching fresh signals from the web. Check back in a minute."
              : "Try adjusting your search or filters"}
          </p>
        </div>
      )}

      {/* Disclaimer */}
      <p className="mt-8 px-1 text-center text-xs text-zinc-600 md:mt-12">
        Rankings blend payment-verified peer benchmarks (where configured), radar signal volume, and
        momentum. Figures are directional — not investment advice.
      </p>

      {openWhy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4">
          <div
            className="relative h-[100dvh] w-full overflow-y-auto border border-[#1F1F1F] bg-[#111111] p-5 sm:h-auto sm:max-h-[85vh] sm:max-w-2xl sm:rounded-2xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpenWhy(null)}
              className="absolute right-4 top-4 rounded-md p-1 text-zinc-500 hover:text-white"
            >
              ✕
            </button>

            {(() => {
              const tier = growthTierForOpportunity(openWhy);
              const hm = HEAT_INLINE[tier] || HEAT_INLINE.stable;
              return (
                <div className="mb-4">
                  <h2 className="mb-1 text-xl font-bold text-white">{openWhy.name}</h2>
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-xs font-medium ${hm.text}`}
                    >
                      {hm.icon}
                      {hm.label}
                    </span>
                    <span className="text-sm text-zinc-400">Market + radar context</span>
                  </div>
                  <p className="mb-4 text-sm text-zinc-400">
                    Why this idea is attracting attention right now — traction, timing, and buyer pain.
                  </p>
                </div>
              );
            })()}

            <div className="space-y-4">
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                <p className="text-sm text-zinc-200 leading-6">
                  {openWhy.insight || "This opportunity stands out due to strong signals and positioning."}
                </p>
              </div>

              <div>
                <p className="text-xs text-zinc-500 uppercase mb-1">Why now</p>
                <p className="text-sm text-zinc-300">
                  {openWhy.why_trending || "Market demand is increasing."}
                </p>
              </div>

              <div>
                <p className="text-xs text-zinc-500 uppercase mb-1">Problem</p>
                <p className="text-sm text-zinc-300">
                  {openWhy.problem_solved || "Clear pain point in the market."}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-1">Target</p>
                  <p className="text-sm text-zinc-300">
                    {openWhy.target_audience || "Niche users"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-1">Business model</p>
                  <p className="text-sm text-zinc-300">
                    {openWhy.business_model || "SaaS"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-1">Difficulty</p>
                  <p className="text-sm text-zinc-300">
                    {openWhy.difficulty || "Medium"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-1">Time to MVP</p>
                  <p className="text-sm text-zinc-300">
                    {openWhy.time_to_mvp || "1-2 months"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunitiesPage;
